import App from './App';
import { fetchUser, otpLogin, renewToken } from './api/userApi';
import './axiosInterceptors';
import { BatchDeterminationContextProvider } from './contexts/BatchDeterminationContext';
import { FileContextProvider } from './contexts/FileContext';
import { OrganizationContextProvider } from './contexts/OrganizationContext';
import { PermissionsContextProvider } from './contexts/PermissionsContext';
import { ProductsContextProvider } from './contexts/ProductsContext';
import type { OTPLoginData, UserJWT } from './interfaces/User';
import Loader from './shared/components/Loader';
import {
	JWT_SESSION_STORAGE,
	ORG_SESSION_STORAGE,
	OTP_SESSION_STORAGE,
	USER_ID_SESSION_STORAGE,
	USER_INFO_SESSION_STORAGE,
} from './shared/constants/constants';
import { initializePendo } from './shared/helpers/pendoInitializer';
import jwt_decode from 'jwt-decode';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const AuthWrapper: React.FC = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [loading, setLoading] = useState<boolean>(true);
	const [userId, setUserId] = useState<OTPLoginData['userId'] | null>(sessionStorage.getItem(USER_ID_SESSION_STORAGE));
	const prevOtpRef = useRef<string | null>(null);
	const [tokenExpiration, setTokenExpiration] = useState<number | null>(null);
	const otp = searchParams.get('otp');
	const org = searchParams.get('org');

	const handleJwtChange = useCallback(() => {
		const userIdChange = sessionStorage.getItem(USER_ID_SESSION_STORAGE);

		if (userId !== userIdChange) {
			setUserId(userIdChange);
		}

		const jwt = sessionStorage.getItem(JWT_SESSION_STORAGE);

		if (jwt) {
			const { exp } = jwt_decode<UserJWT>(jwt);

			if (tokenExpiration !== exp) {
				setTokenExpiration(exp);
			}
		}
	}, [userId, tokenExpiration]);

	// handles state update on jwt session storage change
	useEffect(() => {
		handleJwtChange();

		window.addEventListener('jwt', handleJwtChange);

		return () => window.removeEventListener('jwt', handleJwtChange);
	}, [handleJwtChange]);

	// handles loading state and otp login
	useEffect(() => {
		const login = async () => {
			if (otp && otp !== prevOtpRef.current) {
				sessionStorage.clear();
				sessionStorage.setItem(OTP_SESSION_STORAGE, otp);

				if (org) {
					sessionStorage.setItem(ORG_SESSION_STORAGE, org);
				}

				const otpLoginData = await otpLogin(otp);

				if (otpLoginData) {
					sessionStorage.setItem(JWT_SESSION_STORAGE, otpLoginData.platformToken);
					sessionStorage.setItem(USER_ID_SESSION_STORAGE, otpLoginData.userId);

					const userInfo = await fetchUser(otpLoginData.userId);

					if (userInfo) {
						sessionStorage.setItem(USER_INFO_SESSION_STORAGE, JSON.stringify(userInfo));
					}

					window.dispatchEvent(new Event('jwt'));
				}

				navigate('/', { replace: true });
			}
			if (loading && !otp) {
				setLoading(false);
			}
			initializePendo();
		};
		login();
	}, [navigate, otp, org, loading, handleJwtChange]);

	useEffect(() => {
		prevOtpRef.current = otp;
	}, [otp]);

	// handles token renewal
	useEffect(() => {
		if (tokenExpiration) {
			const timeNowSeconds = Date.now() / 1000;
			const fiveMinSeconds = 300;
			const renewSeconds = tokenExpiration - timeNowSeconds - fiveMinSeconds;

			const renewTimeout = setTimeout(() => {
				renewToken();
			}, renewSeconds * 1000);

			return () => clearTimeout(renewTimeout);
		}
	}, [tokenExpiration]);

	if (loading) {
		return <Loader />;
	}

	if (userId) {
		return (
			<OrganizationContextProvider>
				<PermissionsContextProvider>
					<ProductsContextProvider>
						<FileContextProvider>
							<BatchDeterminationContextProvider>
								<App />
							</BatchDeterminationContextProvider>
						</FileContextProvider>
					</ProductsContextProvider>
				</PermissionsContextProvider>
			</OrganizationContextProvider>
		);
	}

	// redirect to login page if no otp and not logged in
	// Use hostname-based mapping to prevent open redirect (CWE-601) and XSS (CWE-80)
	// Avoid using document.referrer as it's untrusted user input
	const hostname = window.location.hostname;
	let loginUrl = 'https://s1.sovos.com';
	if (hostname.startsWith('integration') || hostname.includes('int')) {
		loginUrl = 'https://int-s1.dev.sovos.org';
	} else if (hostname.includes('qa')) {
		loginUrl = 'https://qa-s1.dev.sovos.org';
	} else if (hostname.includes('stg')) {
		loginUrl = 'https://stg-s1.sovos.com';
	} else if (hostname.includes('uat')) {
		loginUrl = 'https://uat-s1.sovos.com';
	} else if (hostname.includes('localhost') || hostname.includes('dev')) {
		loginUrl = 'https://dev-s1.dev.sovos.org';
	}
	window.location.href = loginUrl;

	return null;
};

export default AuthWrapper;
